//! Product Tour:
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./driverTourTheme.css";

const ACTIVE_CLASS = "ig-tour-active";
const OPEN_MENU_CLASS = "ig-tour-open-menu";

let activeCleanupInterval = null;

const getDashboardTourSteps = () => [
  {
    element: "#nav-dashboard",
    popover: {
      title: "Dashboard",
      description: "Your performance hub — track improvement, insights, and achievements.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#nav-ai-interview",
    popover: {
      title: "AI Interview",
      description: "Run full interview simulations — video, phone, chat, or in-person training.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#nav-chat-interview",
    popover: {
      title: "Chat Interview",
      description: "Fast interview drills with instant AI feedback and scoring.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#nav-questions-ai",
    popover: {
      title: "Questions AI",
      description: "Generate tailored interview questions based on your role & difficulty.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#nav-library",
    popover: {
      title: "Library",
      description: "Master frameworks, communication skills, and technical interview concepts.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#nav-analytics",
    popover: {
      title: "Analytics",
      description: "Track performance over time, set goals & unlock achievements as you grow.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#nav-resume-ai",
    popover: {
      title: "Resume AI",
      description: "Create or enhance a global-standard resume tailored to your job goals.",
      side: "right",
      align: "center",
    },
  },
  {
    element: "#menu",
    popover: {
      title: "More",
      description:
        "Customize your experience and manage preferences.\nGet support & quick product guidance anytime.",
      side: "bottom",
      align: "end",
    },
  },
  {
    element: "#welcome-banner",
    popover: {
      title: "Welcome Banner",
      description: "Greet yourself and track your current streak, level, and XP progress at a glance.",
      side: "bottom",
      align: "center",
    },
  },
  {
    element: "#metrics-grid",
    popover: {
      title: "Key Metrics",
      description: "View important statistics, trends, and your performance overview in one place.",
      side: "bottom",
      align: "center",
    },
  },
  {
    element: "#quick-start",
    popover: {
      title: "Quick Start",
      description: "Access shortcuts and recommended actions to jump-start your day’s training.",
      side: "bottom",
      align: "center",
    },
  },
  {
    element: "#activity-feed",
    popover: {
      title: "Activity Feed",
      description: "See your daily interview practice, exercises completed, and recent achievements.",
      side: "top",
      align: "center",
    },
  },
  {
    element: "#progress-desktop",
    popover: {
      title: "Progress Visualization (Desktop)",
      description: "Track your performance trends, goal progress, and daily practice heatmaps on large screens.",
      side: "top",
      align: "center",
    },
  },
  {
    element: "#progress-mobile",
    popover: {
      title: "Progress Visualization (Mobile)",
      description: "Monitor your improvement and daily practice heatmaps on mobile devices.",
      side: "top",
      align: "center",
    },
  },
  {
    element: "#upcoming-sessions",
    popover: {
      title: "Upcoming Sessions",
      description: "Stay on top of scheduled interviews and practice sessions so nothing is missed.",
      side: "top",
      align: "center",
    },
  },
  {
    element: "#quick-action",
    popover: {
      title: "Quick Action Button",
      description: "Instant access to start a session or practice activity with one click.",
      side: "top",
      align: "center",
    },
  },
];

const normalizeTourOptions = (arg) => {
  if (typeof arg === "boolean") return { onlyIfFirstVisit: arg };
  if (arg && typeof arg === "object") return arg;
  return {};
};

const isElementDisplayed = (el) => {
  if (!el) return false;
  const style = window.getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden" || style.opacity === "0") return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const isElementInViewport = (el, margin = 10) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  const vw = window.innerWidth || document.documentElement.clientWidth;
  return rect.bottom >= margin && rect.right >= margin && rect.top <= vh - margin && rect.left <= vw - margin;
};

// Collect steps whose elements exist + are displayed for the current screen.
// (We don't require viewport intersection here; scrolling is handled per-step.)
const waitForAvailableSteps = async (steps, timeoutMs = 5000, intervalMs = 150) => {
  const start = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      const availableSteps = steps.filter((s) => {
        if (!s?.element) return false;
        const el = document.querySelector(s.element);
        return isElementDisplayed(el);
      });

      if (availableSteps.length) return resolve(availableSteps);
      if (Date.now() - start >= timeoutMs) return resolve([]);

      window.setTimeout(tick, intervalMs);
    };

    tick();
  });
};

const startTourInternal = (rawOptions) => {
  const options = normalizeTourOptions(rawOptions);
  const { onlyIfFirstVisit = false, storageKey = "tour_done" } = options;

  if (onlyIfFirstVisit && localStorage.getItem(storageKey)) return;

  if (activeCleanupInterval) clearInterval(activeCleanupInterval);

  document.documentElement.classList.add(ACTIVE_CLASS);

  (async () => {
    const rawSteps = getDashboardTourSteps();
    const steps = await waitForAvailableSteps(rawSteps);

    if (!steps.length) {
      document.documentElement.classList.remove(ACTIVE_CLASS);
      document.documentElement.classList.remove(OPEN_MENU_CLASS);
      return;
    }

    const driverObj = driver({
      showProgress: true,
      overlayOpacity: 0.62,
      allowClose: true,
      smoothScroll: true,
      stagePadding: 10,
      stageRadius: 14,
      popoverOffset: 12,
      popoverClass: "ig-driver-popover",
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Finish",
      onPopoverRender: (popover) => {
        if (popover?.wrapper) {
          // Guarantees our theme is applied on step 1 too.
          popover.wrapper.classList.add("ig-driver-popover");
          popover.wrapper.style.zIndex = "100002";
          popover.wrapper.style.opacity = "1";
          popover.wrapper.style.visibility = "visible";
        }
      },
      onHighlightStarted: (element, step) => {
        if (step?.element === "#menu") document.documentElement.classList.add(OPEN_MENU_CLASS);
        else document.documentElement.classList.remove(OPEN_MENU_CLASS);

        // If the element isn't in view (common with scroll containers), bring it into view
        // *before* the user sees a popover pointing to nowhere.
        if (element && !isElementInViewport(element, 16)) {
          try {
            element.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
          } catch (e) {}
        }
      },
      onDeselected: () => document.documentElement.classList.remove(OPEN_MENU_CLASS),
      onDestroyed: () => {
        document.documentElement.classList.remove(ACTIVE_CLASS);
        document.documentElement.classList.remove(OPEN_MENU_CLASS);
      },
      steps,
    });

    driverObj.drive();
  })();

  activeCleanupInterval = window.setInterval(() => {
    const overlayExists = Boolean(document.querySelector(".driver-overlay"));
    if (!overlayExists) {
      clearInterval(activeCleanupInterval);
      activeCleanupInterval = null;
      document.documentElement.classList.remove(ACTIVE_CLASS);
      document.documentElement.classList.remove(OPEN_MENU_CLASS);
      localStorage.setItem(storageKey, "true");
    }
  }, 350);
};

export const startTour = () => startTourInternal({ onlyIfFirstVisit: false });
export const startTourIfFirstVisit = () => startTourInternal({ onlyIfFirstVisit: true, storageKey: "tour_done" });